import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export const useSEO = ({ 
  title, 
  description, 
  keywords, 
  image, 
  canonical,
  structuredData 
}) => {
  const location = useLocation();
  
  useEffect(() => {
    // Track page views for SEO analytics
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('config', 'GA_MEASUREMENT_ID', {
        page_title: title,
        page_location: window.location.href,
        page_path: location.pathname
      });
    }
    
    // Update document title for better UX
    if (title) {
      document.title = title;
    }
    
    // Preload critical resources
    const preloadLink = document.createElement('link');
    preloadLink.rel = 'preload';
    preloadLink.href = image;
    preloadLink.as = 'image';
    if (image && !document.querySelector(`link[href="${image}"]`)) {
      document.head.appendChild(preloadLink);
    }
    
    return () => {
      // Cleanup if needed
    };
  }, [title, location.pathname, image]);
  
  return null;
};

export const usePageTracking = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Track page changes for SEO analytics
    const trackPageView = () => {
      if (typeof window !== 'undefined') {
        // Google Analytics 4
        if (window.gtag) {
          window.gtag('event', 'page_view', {
            page_title: document.title,
            page_location: window.location.href,
            page_path: location.pathname
          });
        }
        
        // Microsoft Clarity (if implemented)
        if (window.clarity) {
          window.clarity('set', 'page', location.pathname);
        }
        
        // Facebook Pixel (if implemented)
        if (window.fbq) {
          window.fbq('track', 'PageView');
        }
      }
    };
    
    // Small delay to ensure DOM is ready
    const timer = setTimeout(trackPageView, 100);
    
    return () => clearTimeout(timer);
  }, [location.pathname]);
};

export const useStructuredData = (data) => {
  useEffect(() => {
    if (!data) return;
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(data);
    script.id = 'structured-data-script';
    
    // Remove existing structured data script
    const existingScript = document.getElementById('structured-data-script');
    if (existingScript) {
      document.head.removeChild(existingScript);
    }
    
    document.head.appendChild(script);
    
    return () => {
      const scriptToRemove = document.getElementById('structured-data-script');
      if (scriptToRemove) {
        document.head.removeChild(scriptToRemove);
      }
    };
  }, [data]);
};